import React, { useLayoutEffect, useState, useCallback } from "react";
import cn from "classnames";
import { type ErrorPosition } from "../../enums/errorPositions";
import { useModalContext } from "../modal/hooks/useModalContext";

import "./validationErrorPrompt.scss";
import { createPortal } from "react-dom";

export type ValidationErrorPromptProps = {
  errorPosition: ErrorPosition;
  text: string;
  errorClassName?: string;
  innerRef: React.RefObject<HTMLElement>;
};

interface Position {
  top: number;
  left: number;
  width: string | number;
  height: string | number;
}

const OFFSET = 16; // 1em equivalent in pixels

function calculatePosition(targetRect: DOMRect, errorPosition: ErrorPosition): Position {
  switch (errorPosition) {
    case "bottom":
      return {
        top: targetRect.bottom + OFFSET,
        left: targetRect.left,
        width: targetRect.width,
        height: "auto",
      };
    case "top":
      return {
        top: targetRect.top - OFFSET,
        left: targetRect.left,
        width: targetRect.width,
        height: "auto",
      };
    case "left":
      return {
        top: targetRect.top + targetRect.height / 2,
        left: targetRect.left - OFFSET,
        height: targetRect.height,
        width: "auto",
      };
    case "right":
    default:
      return {
        top: targetRect.top + targetRect.height / 2,
        left: targetRect.right + OFFSET,
        height: targetRect.height,
        width: "auto",
      };
  }
}

export default function ValidationErrorPrompt({
  errorPosition,
  errorClassName,
  text,
  innerRef,
}: ValidationErrorPromptProps) {
  const [position, setPosition] = useState<Position | null>(null);
  const isInModal = useModalContext();

  const updatePosition = useCallback(() => {
    const targetElement = innerRef?.current;
    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      setPosition(calculatePosition(rect, errorPosition));
    }
  }, [innerRef, errorPosition]);

  useLayoutEffect(() => {
    if (isInModal) {
      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
    return undefined;
  }, [updatePosition, isInModal]);

  // Force a position update after a short delay to ensure DOM is ready (only for modal mode)
  useLayoutEffect(() => {
    if (isInModal) {
      const timer = requestAnimationFrame(updatePosition);
      return () => cancelAnimationFrame(timer);
    }
    return undefined;
  }, [updatePosition, isInModal]);

  if (isInModal) {
    if (!position) {
      return null;
    }

    const style: React.CSSProperties = {
      position: "fixed",
      top: position.top,
      left: position.left,
      width: position.width,
      height: position.height,
    };

    return createPortal(
      <div
        className={cn("error-message validation", "in-modal", errorPosition, errorClassName)}
        style={style}
        data-hook-id="error-message"
        data-testid="error-message"
        dangerouslySetInnerHTML={{ __html: `<span>${text}</span>` }}
      />,
      document.body,
    );
  }

  // Fallback to original inline rendering when not in a modal
  return (
    <div
      className={cn("error-message validation", errorPosition, errorClassName)}
      data-hook-id="error-message"
      data-testid="error-message"
      dangerouslySetInnerHTML={{ __html: `<span>${text}</span>` }}
    />
  );
}
